import {
  DeleteOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  UploadOutlined
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Form,
  Grid,
  Input,
  message,
  Space,
  Splitter,
  Typography,
  Upload
} from "antd";
import type { RcFile, UploadFile, UploadProps } from "antd/es/upload";
import Dragger from "antd/es/upload/Dragger";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ErrorCategory } from "../../../Utils/errorClassifier";
import { getToken } from "../../../Utils/recaptcha";
import "./index.scss";
declare global {
  interface Window {
    lintrk?: (action: string, data: Record<string, unknown>) => void;
  }
}
const { TextArea } = Input;
const { Text, Paragraph, Title } = Typography;
const { useBreakpoint } = Grid;

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes (5,242,880 bytes)
const MIN_JOB_DESCRIPTION_LENGTH = 100;
const MAX_JOB_DESCRIPTION_LENGTH = 10000;

// Only PDF allowed for free screening
const ALLOWED_FILE_TYPE = "application/pdf";

interface ScreeningFormValues {
  job_description: string;
  resume: UploadFile[];
}

interface ScreeningFormProps {
  onSubmit: (params: {
    jobDescription: string;
    resumeFile: File;
    captchaToken: string;
  }) => Promise<void>;
  isSubmitting: boolean;
  initialJobDescription?: string;
  errorState?: {
    category?: ErrorCategory;
    headline?: string;
    body?: string;
    guidance?: string[];
    requiresUserAction?: boolean;
  } | null;
}



const ScreeningForm: React.FC<ScreeningFormProps> = ({
  onSubmit,
  isSubmitting,
  initialJobDescription,
  errorState,
}) => {
  const isSubmittingRef = useRef(false);

  const [form] = Form.useForm<ScreeningFormValues>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    if (initialJobDescription) {
      form.setFieldsValue({ job_description: initialJobDescription });
    }
  }, []);  // run once on mount — form instance is stable

  // Clear resume on resume-specific errors (requiresUserAction: true)
  useEffect(() => {
    if (errorState && errorState.requiresUserAction && ['resume_unreadable', 'resume_format', 'resume_language'].includes(errorState.category || '')) {
      handleRemove();
    }
  }, [errorState]);


  const handleSubmit = async (values: ScreeningFormValues) => {
    if (isSubmittingRef.current) return; // 🛡️ block double-click immediately
    isSubmittingRef.current = true;
    console.log("Form submitted with values:", values);
    try {
      const token = await getToken('free_profile_screening_submit');
      //console.log('reCAPTCHA v3 token:', token);
      const trimmedJD = values.job_description?.trim() ?? "";
      const resumeItem = values.resume?.[0] || fileList[0];
      if (!resumeItem || !resumeItem.originFileObj) {
        message.error("Please upload a valid resume file.");
        return;
      }
      const resumeFile = resumeItem.originFileObj as File;
      sessionStorage.setItem("screening_resume_file_name", resumeFile.name);

      await onSubmit({
        jobDescription: trimmedJD,
        resumeFile,
        captchaToken: token,
      });
      window.lintrk?.('track', { conversion_id: 26752433 });
      //message.success('Screening submitted successfully!');
    } catch (err) {
      console.error("reCAPTCHA or submit error:", err);
      message.error(
        "Unable to verify captcha or submit the request. Please try again."
      );
    } finally {
      isSubmittingRef.current = false; // ✅ always reset, even on error
    }
  };



  const beforeUpload: UploadProps["beforeUpload"] = (file: File) => {
    // Validate file type - only PDF allowed
    if (file.type !== ALLOWED_FILE_TYPE) {
      message.error("Only PDF resumes are allowed for free screening");
      return Upload.LIST_IGNORE;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      message.error("Resume must not exceed 5MB");
      return Upload.LIST_IGNORE;
    }

    // File is valid, add to form
    const newFile: UploadFile = {
      uid: file.name,
      name: file.name,
      status: "done",
      originFileObj: file as RcFile,
    };
    setFileList([newFile]);
    form.setFieldValue("resume", [newFile]);
    return false; // Prevent automatic upload
  };

  const handleFileChange: UploadProps["onChange"] = (info) => {
    const { fileList: newFileList } = info;
    setFileList(newFileList);
    form.setFieldValue("resume", newFileList);
  };

  const handleRemove = () => {
    setFileList([]);
    form.setFieldValue("resume", []);
  };

  return (
    <div className="profile-screening-form-container">
      <div className="profile-screening-header">
        <Title level={3} className="profile-screening-title">
          <span className="ai-stars-animated-icon">
            <img
              src="/icons/ai-stars.svg"
              alt="AI"
              className="ai-stars-svg"
              style={{ width: '24px', height: '24px', marginRight: '8px' }}
            />
          </span>
          {/* 
            Suggested catchy names (choose one that best fits your theme):
              - AI Resume Match Analyzer
              - Instant Resume Fit Checker
              - Smart CV Match Insights
              - Job Match AI Scanner
              - Resume & JD Match Wizard
              - TalentFit AI Screener
              - FastTrack Match Analyzer
              - Jobzy AI Fit Studio
          */}
          Try Free Skill Gap Analysis
        </Title>
        <Typography.Paragraph type="secondary" className="profile-screening-subtitle">
          Our AI reveals what resumes don’t show, helping you hire smarter and faster.
        </Typography.Paragraph>
      </div>

      <Form
        form={form}
        name="screening-form"
        onFinish={handleSubmit}
        layout="vertical"
        scrollToFirstError
        className="screening-form"
      >
        <Splitter
          layout={isMobile ? "vertical" : "horizontal"}
          style={{ width: "100%" }}
          className="screening-splitter"
        >
          {/* Left Panel: Job Description */}
          <Splitter.Panel defaultSize={isMobile ? "50%" : "65%"}>
            <Form.Item
              label={
                <Space>
                  <FileTextOutlined />
                  <Text className="form-label-text">Job Description</Text>
                </Space>
              }
              name="job_description"
              rules={[
                { required: true, message: "Job description is required" },
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.resolve();
                    }
                    const trimmed = value.trim();
                    if (trimmed.length < MIN_JOB_DESCRIPTION_LENGTH) {
                      return Promise.reject(
                        `Job description must be at least ${MIN_JOB_DESCRIPTION_LENGTH} characters`
                      );
                    }
                    if (trimmed.length > MAX_JOB_DESCRIPTION_LENGTH) {
                      return Promise.reject(
                        `Job description must not exceed ${MAX_JOB_DESCRIPTION_LENGTH} characters`
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <TextArea
                rows={12}
                placeholder="Paste the full job description here including requirements, responsibilities, and qualifications..."
                className="job-description-textarea"
              />
            </Form.Item>
          </Splitter.Panel>

          {/* Right Panel: Resume Upload */}
          <Splitter.Panel>
            <Form.Item
              label={
                <Space>
                  <UploadOutlined />
                  <Text className="form-label-text">Drag & Drop Candidate Resume</Text>
                </Space>
              }
              name="resume"
              rules={[
                {
                  required: true,
                  message: "Resume file is required",
                },
                {
                  validator: (_, fileList) => {
                    if (!fileList || fileList.length === 0) {
                      return Promise.reject("Please upload a resume");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Dragger
                name="resume"
                accept=".pdf"
                fileList={fileList}
                beforeUpload={beforeUpload}
                onChange={handleFileChange}
                onRemove={handleRemove}
                maxCount={1}
                showUploadList={false}
                className={`resume-upload-dragger${fileList.length > 0 ? " resume-upload-dragger--uploaded" : ""}`}
              >
                {fileList.length > 0 ? (
                  <div
                    className="resume-uploaded-preview"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FilePdfOutlined className="resume-uploaded-preview__icon" />
                    <div className="resume-uploaded-preview__info">
                      <Text
                        strong
                        className="resume-uploaded-preview__name"
                        ellipsis={{ tooltip: fileList[0].name }}
                      >
                        {fileList[0].name}
                      </Text>
                      <Text type="secondary" className="resume-uploaded-preview__size">
                        {fileList[0].originFileObj
                          ? `${(fileList[0].originFileObj.size / 1024).toFixed(1)} KB`
                          : "PDF"}
                      </Text>
                    </div>
                    <button
                      type="button"
                      className="resume-uploaded-preview__delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove();
                      }}
                      aria-label="Remove resume"
                    >
                      <DeleteOutlined />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="ant-upload-drag-icon">
                      <div className="resume-upload-animated-icon">
                        <svg
                          className="resume-upload-svg"
                          viewBox="0 0 64 64"
                          aria-hidden="true"
                        >
                          <g className="resume-upload-svg-pulse">
                            <rect x="18" y="12" width="24" height="32" rx="3" />
                            <polyline points="18,20 42,20" />
                            <polyline points="18,26 36,26" />
                            <polyline points="18,32 34,32" />
                            <g className="resume-upload-arrow">
                              <path d="M32 40v12" />
                              <polyline points="26,46 32,40 38,46" />
                              <rect x="24" y="52" width="16" height="2" rx="1" />
                            </g>
                          </g>
                        </svg>
                      </div>
                    </div>
                    <p className="ant-upload-hint">
                      Upload PDF files only. 
                    </p>
                    <p className="ant-upload-hint">
                    Maximum file size allowed is 5 MB.
                    </p>
                  </>
                )}
              </Dragger>
            </Form.Item>
          </Splitter.Panel>
        </Splitter>

        {/* Error Alert */}
        {errorState && errorState.headline && (
          <Form.Item style={{ margin: '16px' }}>
            <Alert
              type="error"
              showIcon
              message={errorState.headline}
              description={
                <div>
                  <p>{errorState.body}</p>
                  {errorState.guidance && errorState.guidance.length > 0 && (
                    <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                      {errorState.guidance.map((item, idx) => (
                        <li key={idx} style={{ marginBottom: '4px' }}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              }
            />
          </Form.Item>
        )}

        {/* Submit Button */}
        <Form.Item className="form-submit-item">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            icon={<ThunderboltOutlined />}
            className=" button-shine"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Analyze Candidate
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default ScreeningForm;